/** @jsx jsx */
import React, { useCallback, type MouseEvent, type KeyboardEvent, useContext } from 'react';
import { css, jsx } from '@compiled/react';
import { v4 as uuid } from 'uuid';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import InteractionContext from '@atlaskit/react-ufo/interaction-context';
import { token } from '@atlaskit/tokens';
import { toFlagId, useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import messages from '../../common/messages.tsx';
import {
	type AsyncExportIssuesCommonInput,
	isAsyncExportItem,
	type ExportItemType,
} from '../../common/types.tsx';
import { appendExportIssuesRelPolicy } from '../../common/utils.tsx';
import {
	ExportIssuesState,
	useExportIssuesState,
} from '../../controllers/export-issues-state/index.tsx';
import { CREATE_DASHBOARD_GADGET } from '../../services/export-issues/constants.tsx';
import { ExportIssuesTaskFlag } from './export-issues-task-flag/index.tsx';

type Props = {
	exportItem: ExportItemType;
	appCount: number;
	commonInput: AsyncExportIssuesCommonInput;
	onOpenCreateDashboardGadgetModal: () => void;
};

const menuItemStyles = css({
	paddingTop: token('space.075'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
});

export const ExportItem = ({
	exportItem,
	appCount,
	commonInput,
	onOpenCreateDashboardGadgetModal,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag, dismissFlag } = useFlagsService();
	const isAsync = isAsyncExportItem(exportItem);
	const [{ exportsInProgress }, { addExportInProgress, removeExportInProgress }] =
		useExportIssuesState();

	const dashboardGadgetModalContext = useContext(InteractionContext);

	const dashboardGadgetModalInstrumentedHandler = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			if (dashboardGadgetModalContext && dashboardGadgetModalContext.tracePress) {
				dashboardGadgetModalContext.tracePress('create-dashboard-gadget-modal', event.timeStamp);
			}
		},
		[dashboardGadgetModalContext],
	);

	const onClickDropDownItem = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			if (isAsync) {
				const existingExportItem = exportsInProgress.get(exportItem.id);
				const flagId = existingExportItem?.flagId || toFlagId(uuid());
				/**
				 * We only want one export per export type to be in progress at a time.
				 */
				if (!existingExportItem) {
					showFlag({
						id: flagId,

						render: ({ key }) => {
							return (
								/**
								 * Flags are appended to the DOM in a different location than Export Issues button
								 * so we need to wrap the ExportIssuesState using the same scope to ensure the state is shared
								 */
								<ExportIssuesState scope="ExportIssuesState" key={key}>
									<ContextualAnalyticsData
										attributes={{
											exportId: exportItem.id,
											exportLabel: exportItem.label,
											exportType: exportItem.exportType,
											maxResults: exportItem.maxResults,
										}}
									>
										<ExportIssuesTaskFlag
											flagId={flagId}
											exportItem={exportItem}
											onDismissFlag={dismissFlag}
											commonInput={commonInput}
										/>
									</ContextualAnalyticsData>
								</ExportIssuesState>
							);
						},
					});

					addExportInProgress({ exportName: exportItem.id, flagId, commonInput });
				} else {
					fireOperationalAnalytics(createAnalyticsEvent({}), 'JiraIssueExportTask duplicated', {
						sameInput: existingExportItem.commonInput === commonInput,
					});

					const errorFlagId = `${flagId}-error`;
					showFlag({
						type: 'error',
						id: errorFlagId,
						title: formatMessage(messages.exportIssuesAlreadyInProgressTitle, {
							exportItemLabel: exportItem.label,
						}),
						description: formatMessage(messages.exportIssuesAlreadyInProgressDescription),
						messageId:
							'issue-navigator-action-export-issues.ui.export-item.show-flag.error.export-issues-already-in-progress',
						messageType: 'transactional',
						actions: [
							{
								content: formatMessage(messages.exportIssuesAlreadyInProgressCancelExportAction),
								onClick: () => {
									removeExportInProgress(exportItem.id);
									dismissFlag(flagId);
									dismissFlag(errorFlagId);

									const cancelEvent = createAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
									});
									fireUIAnalytics(cancelEvent, 'cancelJiraIssueExportTask');
								},
							},
						],
					});
				}
			}

			if (exportItem.id === CREATE_DASHBOARD_GADGET) {
				dashboardGadgetModalInstrumentedHandler(event);
				onOpenCreateDashboardGadgetModal();
			}

			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
			});
			fireUIAnalytics(analyticsEvent, 'dropdownItem clicked', 'export', {
				dropdownItem: exportItem.id ?? 'app',
				dropdownItemName: exportItem.label,
				appCount,
			});
		},
		[
			isAsync,
			exportItem,
			createAnalyticsEvent,
			appCount,
			exportsInProgress,
			showFlag,
			addExportInProgress,
			dismissFlag,
			commonInput,
			formatMessage,
			removeExportInProgress,
			dashboardGadgetModalInstrumentedHandler,
			onOpenCreateDashboardGadgetModal,
		],
	);

	const isLinkItem = exportItem.id !== CREATE_DASHBOARD_GADGET && !isAsync;

	const isVREnabled = isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons');

	const isHierarchyEnabled =
		// eslint-disable-next-line jira/ff/no-preconditioning
		expVal('jira_spreadsheet_component_m1', 'isHierarchyEnabled', false) &&
		fg('jsc_m2_hierarchy_fe_changes');

	return (
		<DropdownItem
			onClick={onClickDropDownItem}
			{...(isLinkItem && {
				href: exportItem.link ?? '#',
				target: '_blank',
				rel: appendExportIssuesRelPolicy(exportItem.rel),
			})}
		>
			{isVREnabled && isHierarchyEnabled ? (
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				<div css={menuItemStyles} data-testid="issue-navigator-action-export-issues.ui.span">
					{exportItem.label}
				</div>
			) : (
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				<span data-testid="issue-navigator-action-export-issues.ui.span">{exportItem.label}</span>
			)}
		</DropdownItem>
	);
};
